import type { TextFieldProps as AriaTextFieldProps, ValidationResult } from "react-aria-components";
import { Group, TextField } from "react-aria-components";
import React from "react";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";

import { FieldError2 } from "../atoms/FieldError";
import { Input2 } from "../atoms/Input";
import type { Input2Props } from "../atoms/Input";
import { Label2 } from "../atoms/Label";
import { Text2 } from "../atoms/Text";
import { TextArea2 } from "../atoms/TextArea";
import type { TextArea2Props } from "../atoms/TextArea";

export type TextFieldProps = TextFieldInputProps | TextFieldTextAreaProps;

type BaseTextFieldProps = {
  className?: string;
  description?: string;
  errorMessage?: ((validation: ValidationResult) => string) | string;
  hasStrongLabel?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  label: string;
  placeholder?: string;
} & AriaTextFieldProps &
  VariantProps<typeof inputStyles>;

type TextFieldInputProps = {
  icon?: React.ReactNode;
  mode: "input";
  textAreaProps?: never;
  textInputProps?: Input2Props;
} & BaseTextFieldProps;

type TextFieldTextAreaProps = {
  mode: "textarea";
  textAreaProps?: TextArea2Props;
  textInputProps?: never;
} & BaseTextFieldProps;

// TODO: Pass sizes to input
export function TextField2(props: TextFieldProps) {
  const {
    className,
    description,
    errorMessage,
    hasStrongLabel,
    isInvalid,
    isReadOnly,
    isRequired,
    label,
    mode = "input",
    textAreaProps: { className: classNameArea, ...otherAreaProps } = {},
    textInputProps: { className: classNameInput, ...otherInputProps } = {},
    ...otherProps
  } = props;

  const Icon = props.mode === "input" ? props.icon : undefined;

  const { field } = inputStyles();
  const variant = isReadOnly ? "readOnly" : "default";

  return (
    <TextField className={cn(field(), className)} isInvalid={isInvalid} {...otherProps}>
      <Label2
        className={hasStrongLabel ? "tw:font-bold" : ""}
        isInvalid={isInvalid}
        isRequired={isRequired}
        label={label}
      />

      {mode === "input" && (
        <Group className="tw:relative tw:flex tw:w-full tw:items-center">
          <Input2
            className={cn("tw:w-full", Icon && "tw:pr-9", classNameInput)}
            variant={variant}
            {...otherInputProps}
          />
          {Icon && (
            <span className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:right-2.5 tw:-translate-y-1/2">
              {Icon}
            </span>
          )}
        </Group>
      )}

      {mode === "textarea" && (
        <TextArea2 className={classNameArea} variant={variant} {...otherAreaProps} />
      )}

      <FieldError2>{errorMessage}</FieldError2>
      {description && !isInvalid && <Text2 slot="description" text={description} />}
    </TextField>
  );
}

export const inputStyles = tv({
  defaultVariants: {
    size: "md",
  },
  slots: {
    field: "tw:flex tw:flex-col tw:gap-1.5",
  },
});
